import { WeatherMoon20Regular, WeatherSunny20Regular } from '@fluentui/react-icons';
import { ThemeProvider, useTheme } from 'next-themes';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@web/shared/ui/button';

/**
 * Where the person's choice is kept. `index.html` reads the same key before the first paint
 * (so a dark session never flashes light while the app boots) — change both together.
 */
export const THEME_STORAGE_KEY = 'crm-theme';

/**
 * The color theme of the app: light or dark, set as the `dark` class on `<html>` (the
 * `.dark` ramp of `styles.css`). Light until the person turns the dark one on — the system's
 * preference is not followed. A choice saved as `system` (before that rule) reads as light.
 */
export function AppThemeProvider({ children }: { children: ReactNode }) {
  // Before the provider reads it: a saved `system` (or anything else) is no choice — light.
  useState(forgetLegacyChoice);
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem={false}
      themes={['light', 'dark']}
      storageKey={THEME_STORAGE_KEY}
      disableTransitionOnChange
      // The provider's own pre-paint script never runs in a client-rendered app (the one in
      // `index.html` does that job); a data type keeps React from warning about it.
      scriptProps={{ type: 'application/json' }}
    >
      <ThemeColorMeta />
      {children}
    </ThemeProvider>
  );
}

/** The browser bar color (`<meta name="theme-color">`, `index.html`) follows the chosen theme. */
function ThemeColorMeta() {
  const { resolvedTheme } = useTheme();
  useEffect(() => {
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', resolvedTheme === 'dark' ? '#1f1f1f' : '#fcfcfc');
  }, [resolvedTheme]);
  return null;
}

/** Whether the dark theme is on, and the way to turn it on or off. */
export function useDarkTheme(): [dark: boolean, setDark: (dark: boolean) => void] {
  const { resolvedTheme, setTheme } = useTheme();
  return [resolvedTheme === 'dark', (dark) => setTheme(dark ? 'dark' : 'light')];
}

/**
 * The top bar switch between the light and the dark theme. It is a toggle button: pressed
 * means dark. The icon shows the theme the click leads to.
 */
export function ThemeToggle() {
  const { t } = useTranslation();
  const { resolvedTheme, setTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const Glyph = dark ? WeatherSunny20Regular : WeatherMoon20Regular;
  return (
    <Button
      variant="ghost"
      aria-label={t('shell.darkTheme')}
      aria-pressed={dark}
      title={t(dark ? 'shell.useLightTheme' : 'shell.useDarkTheme')}
      onClick={() => setTheme(dark ? 'light' : 'dark')}
      className="text-fg-secondary size-12 rounded-none p-0"
    >
      <Glyph className="size-5" />
    </Button>
  );
}

/** Drops a saved theme other than light or dark (the `system` of the old default). */
function forgetLegacyChoice(): null {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved && saved !== 'light' && saved !== 'dark') localStorage.removeItem(THEME_STORAGE_KEY);
  } catch {
    // No storage (a private window): nothing was saved either.
  }
  return null;
}
